import React from 'react';
import { Sparkles } from 'lucide-react';
import { LLMModelSelector } from '../selectors';
import { CompactSelect } from '../common/CompactSelect';
import { useTranslation } from '@/hooks/useTranslation';
import { useLLMModels } from '@/hooks/useLLMModels';
import { LLM_PROVIDERS } from '@/constants';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AIBoostPanelProps {
  enableAiBoost: boolean;
  llmProvider: string;
  isTranslating: boolean;
  onEnableAiBoostChange: (enabled: boolean) => void;
  onLlmProviderChange: (provider: string) => void;
  onLlmModelChange: (model: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AIBoostPanel: React.FC<AIBoostPanelProps> = ({
  enableAiBoost,
  llmProvider,
  isTranslating,
  onEnableAiBoostChange,
  onLlmProviderChange,
  onLlmModelChange,
}) => {
  const { t } = useTranslation();

  const {
    availableModels,
    selectedModel: llmModel,
    setSelectedModel: setLlmModel,
  } = useLLMModels(llmProvider, undefined, { enabled: enableAiBoost });

  const handleModelChange = (model: string) => {
    setLlmModel(model);
    onLlmModelChange(model);
  };

  return (
    <div className="space-y-4 p-4 border border-purple-500/30 rounded-lg bg-purple-900/20">
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="enableAiBoost"
          checked={enableAiBoost}
          onChange={(e) => onEnableAiBoostChange(e.target.checked)}
          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          disabled={isTranslating}
        />
        <label
          htmlFor="enableAiBoost"
          className="text-sm font-medium text-white/70 flex items-center space-x-2"
        >
          <Sparkles className="h-4 w-4 text-purple-600" />
          <span>
            {t('translate.fields.enableAiBoost', {
              defaultValue: 'AI Boost (LLM Refinement)',
            })}
          </span>
        </label>
      </div>

      <p className="text-xs text-white/60 ml-6">
        {t('translate.fields.aiBoostDescription', {
          defaultValue:
            'Improve translation quality using AI to fix context, pronouns, and ensure natural phrasing. Uses free models by default.',
        })}
      </p>

      {/* LLM Provider and Model Selection */}
      {enableAiBoost && (
        <div className="ml-6 mt-3 space-y-3 p-3 bg-black/20 rounded border border-purple-500/30">
          {/* Provider Selector */}
          <CompactSelect
            label={t('translate.fields.llmProvider', { defaultValue: 'LLM Provider' })}
            options={LLM_PROVIDERS.map((provider) => ({
              value: provider.value,
              label: provider.label,
              disabled: provider.disabled,
            }))}
            value={llmProvider}
            onChange={onLlmProviderChange}
            disabled={isTranslating}
            testId="llm-provider-select"
          />

          {/* Visual Model Selector with Avatars */}
          <LLMModelSelector
            models={
              availableModels.length === 0
                ? [{ value: '', label: 'Loading models...', disabled: true }]
                : availableModels
            }
            value={llmModel}
            onChange={handleModelChange}
            disabled={isTranslating || availableModels.length === 0}
            label={t('translate.fields.llmModel', { defaultValue: 'AI Model' })}
            showFreeOnly={false}
          />
        </div>
      )}
    </div>
  );
};

export default AIBoostPanel;
